import * as React from "react";
import {
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { TagAutocomplete } from "../../../shared-components/TagAutoComplete";
import { enqueueSnackbar } from "notistack";
import FruitifyIcon from "../../../shared-components/FruitifyIcons";
import { FruitifyLoading } from "../../../shared-components/Loading";
import { useApiClient } from "../../../api";
import { MESSAGE_CATEGORIES } from "../../../shared-components/LoadingMessages";

const QuestionContentStep = ({
  subject,
  educationLevel,
  yearGroup,
  existingQuestion,
  onSaveDraft,
  questionType,
  setMarkScheme = () => {},
  disabled = false,
}) => {
  const [questionText, setQuestionText] = useState("");
  const [tags, setTags] = useState([]);
  const [includeSource, setIncludeSource] = useState(true);
  const [sourceText, setSourceText] = useState("");
  const [includeSourceImage, setIncludeSourceImage] = useState(false);
  const [images, setImages] = useState([]);
  const [generating, setGenerating] = useState(false);
  const ApiClient = useApiClient();

  useEffect(() => {
    onSaveDraft({ questionText, tags, sourceText, images });
  }, [questionText, tags, sourceText, images]);

  useEffect(() => {
    if (existingQuestion) {
      setQuestionText(existingQuestion.questionText);
      setSourceText(existingQuestion.sourceText);
      setTags(existingQuestion.tags.map((t) => t.tag));
    }
  }, [existingQuestion]);

  const handleOptionsGenerated = (generatedOptions) => {
    setMarkScheme((prev) => ({
      ...prev, // keep everything else
      options: generatedOptions, // only update options
    }));
  };

  const handleSubmit = async () => {
    setGenerating(true);
    try {
      const response = await ApiClient.question.autoGenerateQuestion({
        subject: subject.id,
        yearGroup: yearGroup.id,
        questionType: questionType,
        includeSourceText: includeSource,
        specificFocus: questionText,
      });
      if (response && response.questionText) {
        setQuestionText(response.questionText);
        setTags(response.suggestedTags);
        setSourceText(response.sourceText || "");
        enqueueSnackbar("Successfully auto suggested question content", {
          variant: "success",
          autoHideDuration: 3000,
        });
        if (response.options) {
          handleOptionsGenerated(response.options);
        }
      } else {
        enqueueSnackbar("There was a problem generating the question", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Error calling autoGenerateQuestion:", error);
      enqueueSnackbar("There was a problem generating the question", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setGenerating(false);
    }
  };

  if (generating) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80%", // make it fill the available space
          width: "100%",
        }}
      >
        <FruitifyLoading category={MESSAGE_CATEGORIES.aiMagic} />
      </Box>
    );
  }

  return (
    <Stack direction={"column"} spacing={2} sx={{ mt: 2 }}>
      <TextField
        multiline
        rows={4}
        label={"Question"}
        placeholder="Enter question or a focus area and let AI suggest one..."
        value={questionText}
        onChange={(e) => setQuestionText(e.target.value)}
        style={{
          minWidth: "100%",
          borderRadius: "4px",
        }}
        disabled={disabled}
      />
      <div
        style={{
          pointerEvents: disabled ? "none" : "auto",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <TagAutocomplete
          value={tags}
          onChange={setTags}
          educationLevel={educationLevel}
          subject={subject}
        />
      </div>
      <FormGroup>
        <FormControlLabel
          control={
            <Switch
              checked={includeSource}
              onChange={(e) => setIncludeSource(e.target.checked)}
              disabled={disabled}
            />
          }
          label="Include a source extract for the students to analyse"
        />
      </FormGroup>
      {includeSource && (
        <TextField
          multiline
          label={"Source extract"}
          rows={4}
          value={sourceText}
          onChange={(e) => setSourceText(e.target.value)}
          disabled={disabled}
        />
      )}

      <>
        <FormGroup>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={includeSourceImage}
                  onChange={(e) => setIncludeSourceImage(e.target.checked)}
                  disabled={disabled}
                />
              }
              label="Include a source image for the students to analyse"
            />
          </Stack>
        </FormGroup>
        {includeSourceImage && (
          <Box
            sx={{
              border: `2px dashed`,
              borderColor: "divider",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              backgroundColor: "transparent",
            }}
          >
            <Typography
              variant={"subtitle1"}
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Choose images to upload
            </Typography>
            <Typography variant={"body2"} color="text.secondary" sx={{ mb: 2 }}>
              JPG, PNG, WEBP up to 10MB each
            </Typography>
            <Button
              variant="contained"
              component="label"
              sx={{ textTransform: "none" }}
              disabled={disabled}
            >
              Choose Images
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setImages(files);
                }}
              />
            </Button>
            {images?.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {images.length} file(s) selected
              </Typography>
            )}
          </Box>
        )}
      </>
      <Typography variant={"subtitle1"}>
        Refine your question and source extract with Fruitify’s AI
      </Typography>
      <Button
        disabled={!questionText || disabled}
        sx={{ maxWidth: 100 }}
        variant={"outlined"}
        endIcon={<FruitifyIcon name={"UseAI"} />}
        onClick={() => handleSubmit()}
      >
        Refine
      </Button>
    </Stack>
  );
};

export { QuestionContentStep };
